/**
 * Hook useSimpleUpdate modifié pour gérer les versions automatiques
 */

import { useState, useEffect } from 'react';
import { getVersionInfo, isNewerVersion } from '../utils/version';

interface SimpleUpdateState {
  showUpdate: boolean;
  isUpdating: boolean;
  newVersion?: string;
  currentVersion: string; // 🆕 AJOUT
}

export const useSimpleUpdate = (): SimpleUpdateState & {
  applyUpdate: () => void;
  dismissUpdate: () => void;
} => {
  const [showUpdate, setShowUpdate] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [newVersion, setNewVersion] = useState<string>();
  
  // 🆕 Récupérer la version actuelle
  const currentVersionInfo = getVersionInfo();

  useEffect(() => {
    // Vérifie si le navigateur supporte les Service Workers
    if (!('serviceWorker' in navigator)) {
      console.warn('[Update] Service Worker non supporté');
      return;
    }

    // Écoute les messages du Service Worker
    const handleMessage = (event: MessageEvent) => {
      const { type, version, timestamp } = event.data || {};
      
      if (type === 'UPDATE_READY') {
        console.log('🔄 Mise à jour détectée !', { 
          currentVersion: currentVersionInfo.full,
          newVersion: version, 
          timestamp 
        });
        
        // 🆕 COMPARAISON INTELLIGENTE DE VERSIONS
        if (version && isNewerVersion(currentVersionInfo.full, version)) {
          setNewVersion(version);
          setShowUpdate(true);
          console.log(`📦 Nouvelle version disponible: ${currentVersionInfo.full} → ${version}`);
        } else if (version && version !== currentVersionInfo.full) {
          // Version différente mais pas forcément plus récente
          setNewVersion(version);
          setShowUpdate(true);
          console.log(`🔄 Version différente détectée: ${currentVersionInfo.full} → ${version}`);
        } else {
          console.log('ℹ️ Même version ou version plus ancienne, pas de notification');
        }
      }
    };

    // Ajoute l'écouteur d'événements
    navigator.serviceWorker.addEventListener('message', handleMessage);
    
    // Nettoyage
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [currentVersionInfo.full]); // 🆕 Dépendance sur la version actuelle

  /**
   * Applique la mise à jour en rechargeant l'application
   */
  const applyUpdate = () => {
    console.log('🔄 Application de la mise à jour...');
    setIsUpdating(true);
    
    // Petit délai pour l'UX puis rechargement
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  /**
   * Rejette temporairement la mise à jour
   */
  const dismissUpdate = () => {
    console.log('⏰ Mise à jour reportée');
    setShowUpdate(false);
  };

  return {
    showUpdate,
    isUpdating,
    newVersion,
    currentVersion: currentVersionInfo.display, // 🆕 Version actuelle
    applyUpdate,
    dismissUpdate
  };
};

export default useSimpleUpdate;